import { generators, TouchMacroCodec, GamepadMacroCodec } from '@playcastdotio/Messaging';
import { ipcSend } from '../../../api/messaging/protocols/ipc';
import { driverState } from '../../../api/stores/shared';
import {
  parseMacroStep,
  type ParsedMacroStep,
  type ParsedMouseStep,
  type ParsedTouchStep,
} from './MacroStepParser';
import { MacroKeyboardEmitter } from './input/MacroKeyboardEmitter';
import { MacroGamepadEmitter } from './input/MacroGamepadEmitter';
import {
  RealMacroMouseEmitter,
  isRecordedClickPair,
  isValidMacroGuestFrame,
  MOUSE_MODE_ABSOLUTE,
  MOUSE_MODE_DEFAULT,
  UnplaceableClickError,
  type MacroGuestFrame,
} from './input/MacroMouseEmitter';
import { MacroTouchEmitter } from './input/MacroTouchEmitter';
import { MacroPlaybackClock } from './input/MacroPlaybackClock';
import { MacroInputReleaseError } from './input/MacroInputReleaseError';
import { MacroStepCleanup } from './MacroStepCleanup';

type ParsedLegacyStep = ParsedMacroStep;

const MIN_CLICK_HOLD_MS = 80;


export type MacroStepRunResult = { success: true } | { success: false; reason: string };

/**
 * Raised when a macro needs an InputCaster (mouse/keyboard) the host reports
 * as unavailable. Mirrors the fail-closed handling of UnplaceableClickError:
 * the macro run aborts loudly instead of silently delivering zero input.
 */
export class MacroInputUnavailableError extends Error {
  public readonly device: 'mouse' | 'keyboard' | 'touch' | 'gamepad';
  public constructor(device: 'mouse' | 'keyboard' | 'touch' | 'gamepad') {
    super(`Macro requires ${device} input but the ${device} InputCaster is unavailable`);
    this.name = 'MacroInputUnavailableError';
    this.device = device;
  }
}

/**
 * Gate macro playback on InputCaster availability. Reads the shared `driverState`
 * store; when a required device is explicitly reported unavailable, returns
 * the device name so the runner can fail loudly.
 *
 * The gate is injectable for tests; by default it consults the live store.
 * When the store has no signal yet (empty / fields undefined — e.g. before
 * the first driver-status poll), the gate passes, so a warm production host
 * that never reported the field doesn't get a false failure.
 */
export type MacroInputGate = (steps: string[]) => 'mouse' | 'keyboard' | 'touch' | 'gamepad' | null;

export function createDriverStateInputGate(): MacroInputGate {
  return (steps: string[]) => {
    const state = driverState.currentState as Partial<{ inputCasterMouse: boolean; inputCasterKeyboard: boolean; touch: boolean; gameController: boolean }>;
    const needsMouse = steps.some((step) => step.startsWith('mouse='));
    const needsKeyboard = steps.some((step) => step.startsWith('key=') || step.startsWith('string='));
    if (needsMouse && state.inputCasterMouse === false) return 'mouse';
    if (needsKeyboard && state.inputCasterKeyboard === false) return 'keyboard';
    if (steps.some(step => step.startsWith('touch=')) && state.touch === false) return 'touch';
    if (steps.some(step => step.startsWith('gpad=')) && state.gameController === false) return 'gamepad';
    return null;
  };
}

export class MacroStepRunner {
  private controller?: AbortController;
  private completion: Promise<boolean> = Promise.resolve(true);
  private releaseConfirmed = true;
  public cancel(): void { this.controller?.abort(); }
  public async cancelAndWait(): Promise<boolean> { this.cancel(); return this.completion; }
  private readonly keyboard = new MacroKeyboardEmitter();
  private readonly gamepad = new MacroGamepadEmitter();
  private readonly touch = new MacroTouchEmitter();

  public constructor(private readonly inputGate: MacroInputGate = createDriverStateInputGate()) {}

  public async runSteps(
    steps: string[],
    label = 'Macro',
    frame?: MacroGuestFrame,
  ): Promise<MacroStepRunResult> {
    this.cancel();
    const controller = new AbortController();
    this.controller = controller;
    const previous = this.completion;
    const operation = previous.then((released): Promise<MacroStepRunResult> | MacroStepRunResult => released
      ? this.runOwned(steps, label, frame, controller.signal)
      : { success: false, reason: 'Macro input release unconfirmed' });
    this.completion = operation.then(() => this.releaseConfirmed, () => this.releaseConfirmed);
    return operation;
  }

  private async runOwned(steps: string[], label: string, frame: MacroGuestFrame | undefined,
    signal: AbortSignal): Promise<MacroStepRunResult> {
    if (signal.aborted) return { success: false, reason: 'Macro cancelled' };
    if (!steps || steps.length === 0) {
      console.log(`[MacroManager] No steps for ${label}, skipping.`);
      return { success: true };
    }

    const blockedDevice = this.inputGate(steps);
    if (blockedDevice) {
      // A recorded macro that needs an unavailable InputCaster must NOT finish
      // as a silent success — that is exactly the "macro succeeded but zero
      // input was delivered" failure mode (G8). Return a typed failure rather
      // than throwing, so callers observe a clean failure result.
      return {
        success: false as const,
        reason: new MacroInputUnavailableError(blockedDevice).message,
      };
    }

    let absoluteModeSent = false;
    if (steps.some(step => step.startsWith('mouse=wheel') && !parseMacroStep(step)))
      return { success: false, reason: 'Invalid mouse wheel macro' };
    if (steps.some(step => step.startsWith('touch=')) && !TouchMacroCodec.validateSequence(steps))
      return { success: false, reason: 'Invalid touch macro sequence' };
    if (steps.some(step => step.startsWith('gpad=') && !parseMacroStep(step)))
      return { success: false, reason: 'Invalid gamepad macro' };
    let mouseDownAtMs: number | null = null;
    const heldMouseButtons = new Set<number>();
    const heldTouches = new Map<number, ParsedTouchStep>();
    const onFirstAbsoluteMode = () => {
      if (absoluteModeSent) return;
      console.log('[MacroManager] setMode absolute before mouse=abs');
      ipcSend(generators.mouse.setMode('host', MOUSE_MODE_ABSOLUTE));
      absoluteModeSent = true;
    };
    const mouseEmitter = new RealMacroMouseEmitter(onFirstAbsoluteMode, label);

    try {
      ipcSend(
        generators.input.toggleFilters('host', {
          keyboard: false,
          mouse: false,
          gamepad: false,
          touch: false,
        }),
      );
      await MacroPlaybackClock.wait(100, signal);

      console.log(`[MacroManager] Running ${label} with ${steps.length} steps.`);
      if (isValidMacroGuestFrame(frame)) {
        console.log(`[MacroManager] ${label} guest frame ${frame.width}x${frame.height}`);
      }

      for (let i = 0; i < steps.length; i += 1) {
        const rawStep = steps[i];
        const step = parseMacroStep(rawStep);
        if (!step) continue;

        if (step.type === 'key') {
          const legacyStep = step as ParsedLegacyStep;
          const keyCode = Number(legacyStep.value);
          if (Number.isNaN(keyCode)) {
            console.warn('[MacroManager] Invalid key code in step:', rawStep);
            continue;
          }
          console.log(`[MacroManager] Sending key press: ${keyCode}`);
          await MacroPlaybackClock.wait(legacyStep.timing, signal);
          await this.keyboard.sendKeyPress(keyCode, false, legacyStep.hold, signal);
        } else if (step.type === 'string') {
          const legacyStep = step as ParsedLegacyStep;
          console.log(`[MacroManager] Sending string: ${legacyStep.value}`);
          await MacroPlaybackClock.wait(legacyStep.timing, signal);
          await this.keyboard.sendKeyString(legacyStep.value, signal);
        } else if (step.type === 'gpad') {
          const legacyStep = step as ParsedLegacyStep;
          console.log(`[MacroManager] Sending gpad: ${legacyStep.value}`);
          await MacroPlaybackClock.wait(legacyStep.timing, signal);
          const next = i + 1 < steps.length ? parseMacroStep(steps[i + 1]) as ParsedMacroStep | null : null;
          const continueState = GamepadMacroCodec.isState(legacyStep.value) && next?.type === 'gpad'
            && GamepadMacroCodec.isState(next.value) && next.timing === 0
            && GamepadMacroCodec.deviceId(legacyStep.value) === GamepadMacroCodec.deviceId(next.value);
          await this.gamepad.sendGamepadTap(legacyStep.value, legacyStep.hold, signal, continueState);
        } else if (step.type === 'mouse') {
          const mouseStep = step as ParsedMouseStep;
          const nextParsed = i + 1 < steps.length ? parseMacroStep(steps[i + 1]) : null;
          const nextMouse = nextParsed?.type === 'mouse' ? nextParsed as ParsedMouseStep : null;

          if (isRecordedClickPair(mouseStep, nextMouse)) {
            await MacroPlaybackClock.wait(mouseStep.timing, signal);
            if (isValidMacroGuestFrame(frame)) heldMouseButtons.add(nextMouse.params.button ?? 0);
            const placed = await mouseEmitter.emitClick(
              { abs: mouseStep, down: nextMouse },
              frame ?? { width: 0, height: 0 },
            );
            if (!placed) {
              console.warn('[MacroManager] Recorded click could not be placed — aborting macro', {
                label,
                frame: frame ?? null,
                normalized: { x: mouseStep.params.x, y: mouseStep.params.y },
              });
              throw new UnplaceableClickError(label);
            }
            mouseDownAtMs = Date.now();
            heldMouseButtons.add(nextMouse.params.button ?? 0);
            i += 1;
            continue;
          }

          await MacroPlaybackClock.wait(mouseStep.timing, signal);
          if (mouseStep.action === 'up' && mouseDownAtMs !== null) {
            const remainingHoldMs = MIN_CLICK_HOLD_MS - (Date.now() - mouseDownAtMs);
            if (remainingHoldMs > 0) await MacroPlaybackClock.wait(remainingHoldMs, signal);
          }
          if (mouseStep.action === 'down') heldMouseButtons.add(mouseStep.params.button ?? 0);
          await mouseEmitter.sendMouseAction(mouseStep, frame);
          if (mouseStep.action === 'up') heldMouseButtons.delete(mouseStep.params.button ?? 0);
          if (mouseStep.action === 'down') mouseDownAtMs = Date.now();
        } else if (step.type === 'touch') {
          const touchStep = step as ParsedTouchStep;
          await MacroPlaybackClock.wait(touchStep.timing, signal);
          const touchId = touchStep.touchId ?? 0;
          if (touchStep.action !== 'up') heldTouches.set(touchId, touchStep);
          await this.touch.sendTouchAction(touchStep);
          if (touchStep.action === 'up') heldTouches.delete(touchId);
        }
      }

      console.log(`[MacroManager] ${label} macro complete.`);
      return { success: true };
    } catch (error) {
      if (error instanceof MacroInputReleaseError) this.releaseConfirmed = false;
      if (signal.aborted) return { success: false, reason: 'Macro cancelled' };
      if (error instanceof UnplaceableClickError || error instanceof MacroInputUnavailableError) {
        return { success: false, reason: error.message };
      }
      console.error('[MacroManager] Failed to run macro steps:', error);
      throw error;
    } finally {
      const confirmed = await MacroStepCleanup.run([
        () => this.gamepad.release(),
        ...Array.from(heldMouseButtons, button => () => mouseEmitter.sendMouseAction(
          { type: 'mouse', action: 'up', timing: 0, params: { button } }, frame)),
        ...Array.from(heldTouches.values(), touch => () => this.touch.sendTouchAction({ ...touch, action: 'up', timing: 0 })),
        () => { if (absoluteModeSent) ipcSend(generators.mouse.setMode('host', MOUSE_MODE_DEFAULT)); },
        () => { ipcSend(generators.input.toggleFilters('host', {
          keyboard: true,
          mouse: true,
          gamepad: true,
          touch: true,
          exitGuestModeAfterAdminSession: false,
        })); },
      ]);
      this.releaseConfirmed = this.releaseConfirmed && confirmed;
      if (!this.releaseConfirmed) throw new Error('Macro input release failed');
    }
  }
}

export type { MacroGuestFrame };
